import { FC, useCallback, useState } from 'react';
import Select, {
  components,
  DropdownIndicatorProps,
  MenuListProps,
} from 'react-select';
import { ChevronDownIcon as ChevronDownComponent } from '@features/ui/icons';
import { TOption } from '@src/types';

const DropdownIndicator = (props: DropdownIndicatorProps) => {
  return (
    <components.DropdownIndicator {...props}>
      <ChevronDownComponent className={'mr-1'} />
    </components.DropdownIndicator>
  );
};

interface InfiniteScrollMultiSelectProps {
  options: TOption[];
  placeholder?: string;
  value?: (string | number)[] | (string | number) | null;
  error?: boolean;
  onChange?: (value: (string | number)[] | (string | number) | null) => void;
  id?: string;
  onMenuScrollToBottom?: () => void;
  isLoading?: boolean;
  hasMore?: boolean;
  isMulti?: boolean;
  onInputChange?: (value: string) => void;
}

const CustomMenuList: FC<MenuListProps<TOption, true>> = (props) => {
  const { children, selectProps } = props;
  const { onMenuScrollToBottom, isLoading, hasMore } = selectProps as any;

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.target as HTMLDivElement;
      const bottom =
        target.scrollHeight - target.scrollTop <= target.clientHeight + 50;

      if (bottom && !isLoading && hasMore && onMenuScrollToBottom) {
        onMenuScrollToBottom();
      }
    },
    [isLoading, hasMore, onMenuScrollToBottom]
  );

  return (
    <components.MenuList
      {...props}
      innerProps={{ ...props.innerProps, onScroll: handleScroll }}
    >
      {children}
      {isLoading && (
        <div className="text-center py-2 text-gray-400 text-sm">Loading...</div>
      )}
      {!hasMore && !isLoading && (
        <div className="text-center py-2 text-gray-500 text-xs">
          No more options
        </div>
      )}
    </components.MenuList>
  );
};

export const InfiniteScrollMultiSelect: FC<InfiniteScrollMultiSelectProps> = ({
  options,
  placeholder,
  value,
  error,
  onChange,
  id,
  onMenuScrollToBottom,
  isLoading,
  hasMore,
  isMulti = true,
  onInputChange,
}) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const customStyles = {
    control: (base: any) => ({
      ...base,
      backgroundColor: 'transparent',
      borderColor: error ? '#ef4444' : '#C6C6C6',
      borderWidth: '1px',
      borderRadius: '3px',
      minHeight: '38px',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#C6C6C6',
      },
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: '#171717',
      color: 'white',
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      color: 'white',
    }),
    singleValue: (base: any) => ({
      ...base,
      color: '#ccc',
    }),
    placeholder: (base: any) => ({
      ...base,
      color: '#A1A1A1FF',
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: '#888',
      padding: '0 8px',
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: '#414141FF',
      zIndex: 20,
      paddingLeft: 4,
      paddingRight: 4,
      borderRadius: 6,
    }),
    menuList: (base: any) => ({
      ...base,
      maxHeight: '300px',
      paddingTop: 0,
      paddingBottom: 0,
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? '#535353' : '#414141FF',
      color: '#ccc',
      padding: '8px 12px',
      paddingTop: 6,
      paddingBottom: 6,
      paddingRight: 8,
      paddingLeft: 8,
      borderRadius: 6,
      cursor: 'pointer',
      '&:active': {
        backgroundColor: '#333',
      },
    }),
    indicatorSeparator: (base: any) => ({
      ...base,
      width: 0,
    }),
  };

  const selectedOptions = isMulti
    ? options.filter((opt) => Array.isArray(value) && value.includes(opt.value))
    : options.find((opt) => opt.value === value) || null;

  return (
    <Select
      components={{
        // @ts-ignore
        DropdownIndicator,
        MenuList: CustomMenuList,
      }}
      id={id}
      isMulti={isMulti}
      isSearchable={true}
      menuIsOpen={menuIsOpen}
      options={options}
      placeholder={placeholder ?? 'Select'}
      styles={customStyles}
      onChange={(selected) => {
        if (isMulti) {
          const values = (selected as TOption[]).map((opt) => opt.value);
          onChange?.(values);
        } else {
          const singleValue = (selected as TOption | null)?.value ?? null;
          onChange?.(singleValue);
        }
      }}
      onInputChange={(value, actionMeta) => {
        if (actionMeta.action === 'input-change') {
          onInputChange?.(value);
        }

        return value;
      }}
      // @ts-ignore
      onMenuClose={() => setMenuIsOpen(false)}
      onMenuOpen={() => setMenuIsOpen(true)}
      isLoading={isLoading}
      // @ts-ignore
      onMenuScrollToBottom={onMenuScrollToBottom}
      value={selectedOptions}
      // @ts-ignore
      hasMore={hasMore}
    />
  );
};
